/**
 * Finds the import that binds a name in a source file, so a cmd-click on the name can open the
 * file it comes from. Each statement form is one row of its language's table: a pattern, the names
 * it binds and the specifier it reads from. Regexes rather than a parser, because the diff renders
 * every language cubic reviews and the import block is all this needs.
 */

export type ImportBinding = {
  /** The module as written, or for Python the file path its dotted module names. */
  specifier: string;
  /** The imported name; `default` locates its default export, null opens the whole module. */
  importedName: string | null;
};

type BoundName = { local: string; importedName: string | null };

type ImportForm = {
  pattern: RegExp;
  bindings: (groups: Record<string, string | undefined>) => BoundName[];
  specifier: (groups: Record<string, string | undefined>) => string;
};

const IDENTIFIER = String.raw`[\p{L}_$][\p{L}\p{N}_$]*`;
const IDENTIFIER_ONLY = new RegExp(`^${IDENTIFIER}$`, 'u');
const QUOTED_SPECIFIER = String.raw`(?<quote>['"])(?<specifier>[^'"\n]+)\k<quote>`;
const FROM = String.raw`\s*from\s*${QUOTED_SPECIFIER}`;
const IMPORT = String.raw`\bimport\s+(?:type\s+)?`;
/** The default name an `import X, { y }` or `import X, * as y` names before the rest. */
const DEFAULT_PREFIX = String.raw`(?:${IDENTIFIER}\s*,\s*)?`;
const REQUIRE = String.raw`\s*=\s*require\(\s*${QUOTED_SPECIFIER}\s*\)`;
const COMMENT = /\/\/[^\n]*|\/\*[\s\S]*?\*\/|#[^\n]*/g;
const BLOCK_COMMENT_CONTINUATION = /^\s*\*/;
const DEFAULT_EXPORT = /\bexport\s+(?:default(?![\p{L}\p{N}_$])|\{(?<names>[^}]*)\})/gu;

const quotedSpecifier = (groups: Record<string, string | undefined>) => groups.specifier ?? '';
const localName = (groups: Record<string, string | undefined>): BoundName[] =>
  groups.local ? [{ local: groups.local, importedName: groups.local }] : [];
const defaultName = (groups: Record<string, string | undefined>): BoundName[] =>
  groups.local ? [{ local: groups.local, importedName: 'default' }] : [];
const wholeModule = (groups: Record<string, string | undefined>): BoundName[] =>
  groups.local ? [{ local: groups.local, importedName: null }] : [];

/** JavaScript and TypeScript, the forms every file but a Python one is read with. */
const ECMASCRIPT_IMPORT_FORMS: readonly ImportForm[] = [
  // import X from 'm', import X, { y } from 'm'
  {
    pattern: new RegExp(
      String.raw`${IMPORT}(?<local>${IDENTIFIER})\s*(?:,\s*(?:\{[^}]*\}|\*\s*as\s+${IDENTIFIER})\s*)?from\s*${QUOTED_SPECIFIER}`,
      'gu',
    ),
    bindings: defaultName,
    specifier: quotedSpecifier,
  },
  // import { a, b as X } from 'm', import type { a } from 'm', across lines
  {
    pattern: new RegExp(String.raw`${IMPORT}${DEFAULT_PREFIX}\{(?<names>[^}]*)\}${FROM}`, 'gu'),
    bindings: (groups) => parseNameList(groups.names, /\s+as\s+/),
    specifier: quotedSpecifier,
  },
  // import * as X from 'm'
  {
    pattern: new RegExp(
      String.raw`${IMPORT}${DEFAULT_PREFIX}\*\s*as\s+(?<local>${IDENTIFIER})${FROM}`,
      'gu',
    ),
    bindings: wholeModule,
    specifier: quotedSpecifier,
  },
  // export { a as X } from 'm'
  {
    pattern: new RegExp(String.raw`\bexport\s+(?:type\s+)?\{(?<names>[^}]*)\}${FROM}`, 'gu'),
    bindings: (groups) => parseNameList(groups.names, /\s+as\s+/),
    specifier: quotedSpecifier,
  },
  // export * as ns from 'm', export type * as ns from 'm'
  {
    pattern: new RegExp(
      String.raw`\bexport\s+(?:type\s+)?\*\s*as\s+(?<local>${IDENTIFIER})${FROM}`,
      'gu',
    ),
    bindings: wholeModule,
    specifier: quotedSpecifier,
  },
  // const X = require('m')
  {
    pattern: new RegExp(String.raw`\b(?:const|let|var)\s+(?<local>${IDENTIFIER})${REQUIRE}`, 'gu'),
    bindings: localName,
    specifier: quotedSpecifier,
  },
  // const { a: X, b } = require('m')
  {
    pattern: new RegExp(String.raw`\b(?:const|let|var)\s*\{(?<names>[^}]*)\}${REQUIRE}`, 'gu'),
    bindings: (groups) => parseNameList(groups.names, /\s*:\s*/),
    specifier: quotedSpecifier,
  },
];

/**
 * Python's forms, read only in Python files: a JavaScript line such as `import Foo,`, which opens
 * a default import wrapped onto the next line, reads as Python's `import Foo`.
 */
const PYTHON_IMPORT_FORMS: readonly ImportForm[] = [
  // from .mod import a as X, from pkg.mod import (a, b)
  {
    pattern:
      /^[\t ]*from\s+(?<module>\.*[\w.]*)\s+import\s+(?:\((?<parenthesized>[^)]*)\)|(?<names>[^\n]*))/gmu,
    bindings: (groups) => parseNameList(groups.parenthesized ?? groups.names, /\s+as\s+/),
    specifier: (groups) => pythonModulePath(groups.module ?? ''),
  },
  // import app.utils as X
  // Without an alias, import app.utils binds the root app module, not utils.
  {
    pattern:
      /^[\t ]*import[\t ]+(?<module>[\w.]+)(?:[\t ]+as[\t ]+(?<local>\w+))?[\t ]*(?=$|[,;\r\n])/gmu,
    bindings: (groups) => wholeModule({ local: groups.local ?? groups.module?.split('.')[0] }),
    specifier: (groups) => {
      const moduleName = groups.module ?? '';

      return pythonModulePath(groups.local ? moduleName : (moduleName.split('.')[0] ?? ''));
    },
  },
];

/**
 * The import in `source` that binds `name`, or null when nothing imports it.
 * Uses `fromPath` to recognize Python hash comments without masking JavaScript private names,
 * and to read a Python file with Python's forms and any other file with JavaScript's. Plain
 * TypeScript allows angle-bracket assertions, so only other non-Python paths mask JSX text.
 */
export function findImportBinding(
  source: string,
  name: string,
  fromPath = 'source.ts',
): ImportBinding | null {
  const { commentsMasked, nonCode } = maskSourceOf(source, fromPath);

  for (const form of isPythonPath(fromPath) ? PYTHON_IMPORT_FORMS : ECMASCRIPT_IMPORT_FORMS) {
    for (const match of commentsMasked.matchAll(form.pattern)) {
      const start = match.index + match[0].search(/\S/);

      if (nonCode[start]) {
        continue;
      }

      const groups = match.groups ?? {};
      const bound = form.bindings(groups).find((binding) => binding.local === name);

      if (bound) {
        return { specifier: form.specifier(groups), importedName: bound.importedName };
      }
    }
  }

  return null;
}

/**
 * The 1-based default export statement line, including export lists, without following barrels.
 * Uses `fromPath` to distinguish TypeScript assertions from JSX text; unknown source permits JSX.
 */
export function findDefaultExportLine(source: string, fromPath = 'source.jsx'): number | null {
  const { commentsMasked, nonCode } = maskSourceOf(source, fromPath);

  for (const match of commentsMasked.matchAll(DEFAULT_EXPORT)) {
    if (nonCode[match.index]) {
      continue;
    }

    const names = match.groups?.names;

    if (
      names !== undefined &&
      !parseNameList(names, /\s+as\s+/).some((binding) => binding.local === 'default')
    ) {
      continue;
    }

    return source.slice(0, match.index).split('\n').length;
  }

  return null;
}

/**
 * Whether `offset` in one line of `fromPath` falls in a comment or a literal, where a word is prose
 * rather than a name the code uses. A line read on its own cannot see a block comment's opening,
 * so its ` * ` continuation lines count as comments too.
 */
export function isProseAt(lineText: string, offset: number, fromPath = 'source.ts'): boolean {
  if (!isPythonPath(fromPath) && BLOCK_COMMENT_CONTINUATION.test(lineText)) {
    return true;
  }

  return maskSourceOf(lineText, fromPath).nonCode[offset] === 1;
}

function isPythonPath(fromPath: string): boolean {
  return /\.pyi?$/i.test(fromPath);
}

/**
 * Masks `source` as its file's language reads it: Python's hash comments, and JSX text everywhere
 * but plain TypeScript, whose angle-bracket assertions would read as tags.
 */
function maskSourceOf(source: string, fromPath: string): ReturnType<typeof maskImportSource> {
  return maskImportSource(source, isPythonPath(fromPath), !/\.[cm]?ts$/i.test(fromPath));
}

/**
 * Keeps quoted specifiers for captures, but marks literals and blanks comments/JSX text at their offsets.
 * `jsxText` disables raw-text masking for plain TypeScript while preserving lexical tag tracking.
 */
function maskImportSource(
  source: string,
  python = false,
  jsxText = true,
): { commentsMasked: string; nonCode: Uint8Array } {
  const commentsMasked = source.split('');
  const nonCode = new Uint8Array(source.length);
  const regexContext = createRegexContext();
  const jsxContext = createJsxTagContext();
  let index = 0;

  while (index < source.length) {
    const character = source[index];

    if (!python && jsxText && jsxContext.inText && character !== '<' && character !== '{') {
      let end = index;

      // Raw children never enter JavaScript's string, comment, regex, or token contexts.
      while (end < source.length && source[end] !== '<' && source[end] !== '{') {
        if (source[end] !== '\n' && source[end] !== '\r') {
          commentsMasked[end] = ' ';
        }
        end += 1;
      }
      nonCode.fill(1, index, end);
      index = end;
      continue;
    }

    const lineComment = source.startsWith('//', index) || (python && character === '#');
    const blockComment = source.startsWith('/*', index);
    const jsxEnd =
      !python && !lineComment && !blockComment
        ? jsxContext.read(source, index, regexContext.canStartRegex)
        : null;
    const regexEnd =
      !python &&
      !lineComment &&
      !blockComment &&
      jsxEnd === null &&
      character === '/' &&
      regexContext.canStartRegex
        ? findRegexLiteralEnd(source, index)
        : null;
    let end = index;

    if (lineComment) {
      while (end < source.length && source[end] !== '\n' && source[end] !== '\r') {
        end += 1;
      }
    } else if (blockComment) {
      const closing = source.indexOf('*/', index + 2);

      end = closing === -1 ? source.length : closing + 2;
    } else if (jsxEnd !== null) {
      nonCode.fill(1, index, jsxEnd);
      // Preserve operator context at opening-tag `>`; a closing tag completes the JSX value.
      regexContext.accept(character === '>' ? '>' : 'literal');
      index = jsxEnd;
      continue;
    } else if (!python && jsxText && jsxContext.inText && character === '<') {
      // An unrecognized tag opener stays text, and must still advance the scanner.
      nonCode[index] = 1;
      commentsMasked[index] = ' ';
      index += 1;
      continue;
    } else if (regexEnd !== null) {
      end = regexEnd;
      regexContext.accept('literal');
    } else if (character === "'" || character === '"' || character === '`') {
      const triple = character !== '`' && source.startsWith(character.repeat(3), index);
      const delimiter = triple ? character.repeat(3) : character;

      end = index + delimiter.length;

      while (end < source.length) {
        if (source[end] === '\\') {
          end = Math.min(end + 2, source.length);
        } else if (source.startsWith(delimiter, end)) {
          end += delimiter.length;
          break;
        } else {
          end += 1;
        }
      }
      regexContext.accept('literal');
    } else {
      if (!python && !/\s/.test(character ?? '')) {
        const token = readRegexContextToken(source, index);

        regexContext.accept(token);
        index += token.length;
      } else {
        index += 1;
      }
      continue;
    }

    nonCode.fill(1, index, end);

    if (lineComment || blockComment) {
      for (let cursor = index; cursor < end; cursor += 1) {
        if (source[cursor] !== '\n' && source[cursor] !== '\r') {
          commentsMasked[cursor] = ' ';
        }
      }
    }

    index = end;
  }

  return { commentsMasked: commentsMasked.join(''), nonCode };
}

const JS_WORD_START = /[\p{L}_$]/u;
const JS_WORD_PART = /[\p{L}\p{N}_$]/u;
const CONTROL_HEADERS = new Set(['if', 'while', 'for', 'with', 'switch', 'catch']);
const EXPRESSION_KEYWORDS = new Set([
  'return',
  'throw',
  'case',
  'delete',
  'typeof',
  'void',
  'yield',
  'await',
  'new',
  'in',
  'instanceof',
  'of',
  'extends',
]);
const STATEMENT_KEYWORDS = new Set(['else', 'do', 'try', 'finally']);
const CONTEXT_TOKEN_PAIRS = new Set(['=>', '?.', '++', '--', '/=']);

/**
 * Match closing tags only after an opening tag, outside its embedded JavaScript expressions.
 * Track raw child text at the tag's brace depth without treating it as JavaScript.
 * A relational `< /pattern/` (even without spaces) must still reach the regex scanner.
 */
function createJsxTagContext() {
  const tags: Array<{ name: string; braceDepth: number; opening: boolean }> = [];
  let braceDepth = 0;

  return {
    get inText() {
      const tag = tags.at(-1);

      return tag !== undefined && !tag.opening && tag.braceDepth === braceDepth;
    },
    read(source: string, index: number, canStartExpression: boolean): number | null {
      const tag = tags.at(-1);
      const atTagDepth = tag?.braceDepth === braceDepth;
      const character = source[index];

      if (character === '<') {
        const text = source.slice(index);
        const closing = /^<\/([\w$.:-]*)\s*>/.exec(text);

        if (closing && tag && atTagDepth && closing[1] === tag.name) {
          tags.pop();
          return index + closing[0].length;
        }
        if (canStartExpression || (tag && atTagDepth && !tag.opening)) {
          // A TSX generic arrow's `<T extends U>` opens type parameters, not a tag.
          const opening = /^<([A-Za-z_$][\w$.:-]*)(?=[\s/>])(?!\s+extends\b)|^<>/.exec(text);

          if (opening) {
            tags.push({ name: opening[1] ?? '', braceDepth, opening: opening[1] !== undefined });
            return index + opening[0].length;
          }
        }
      } else if (tag?.opening && atTagDepth && source.startsWith('/>', index)) {
        tags.pop();
        return index + 2;
      } else if (tag?.opening && atTagDepth && character === '>') {
        tag.opening = false;
        return index + 1;
      } else if (character === '{') {
        braceDepth += 1;
      } else if (character === '}') {
        braceDepth -= 1;
      }

      return null;
    },
  };
}

/** Reads only the significant tokens needed to distinguish a regex opener from division. */
function readRegexContextToken(source: string, start: number): string {
  let end = start + 1;

  if (JS_WORD_PART.test(source[start] ?? '')) {
    while (end < source.length && JS_WORD_PART.test(source[end] ?? '')) {
      end += 1;
    }
  } else if (CONTEXT_TOKEN_PAIRS.has(source.slice(start, end + 1))) {
    end += 1;
  }

  return source.slice(start, end);
}

/** A regex cannot span a line; escaped slashes and slashes inside classes do not close it. */
function findRegexLiteralEnd(source: string, start: number): number | null {
  let inClass = false;

  for (let index = start + 1; index < source.length; index += 1) {
    const character = source[index];

    if (
      character === '\n' ||
      character === '\r' ||
      character === '\u2028' ||
      character === '\u2029'
    ) {
      return null;
    }
    if (character === '\\') {
      const escaped = source[index + 1];

      if (escaped === '\n' || escaped === '\r' || escaped === '\u2028' || escaped === '\u2029') {
        return null;
      }
      index += 1;
    } else if (character === '[') {
      inClass = true;
    } else if (character === ']') {
      inClass = false;
    } else if (character === '/' && !inClass) {
      let end = index + 1;

      while (end < source.length && JS_WORD_PART.test(source[end] ?? '')) {
        end += 1;
      }
      return end;
    }
  }

  return null;
}

type RegexParen = 'control' | 'expression' | 'function-expression' | 'function-declaration';
type RegexBrace = 'object' | 'block' | 'expression-body';

/**
 * Lexical context only, not a parser: literals finish expressions, operators start them, and
 * delimiter stacks distinguish control-header statements from calls and object/function values.
 * Whitespace and comments never enter this state, so they cannot change a slash's meaning.
 */
function createRegexContext() {
  let canStartRegex = true;
  let statementStart = true;
  let previous = '';
  let pendingFunction: RegexParen | null = null;
  let pendingClass: RegexBrace | null = null;
  let body: RegexBrace | null = null;
  const parens: RegexParen[] = [];
  const braces: RegexBrace[] = [];

  return {
    get canStartRegex() {
      return canStartRegex;
    },
    accept(token: string) {
      const nextBody = body;
      const member = previous === '.' || previous === '?.';

      body = null;
      if (token === '(') {
        parens.push(pendingFunction ?? (CONTROL_HEADERS.has(previous) ? 'control' : 'expression'));
        pendingFunction = null;
        canStartRegex = true;
        statementStart = false;
      } else if (token === ')') {
        const kind = parens.pop();

        canStartRegex = kind === 'control';
        statementStart = canStartRegex;
        if (kind === 'function-expression' || kind === 'function-declaration') {
          body = kind === 'function-expression' ? 'expression-body' : 'block';
        }
      } else if (token === '{') {
        const kind =
          nextBody ?? pendingClass ?? (statementStart || !canStartRegex ? 'block' : 'object');

        pendingClass = null;
        braces.push(kind);
        canStartRegex = true;
        statementStart = kind !== 'object';
      } else if (token === '}') {
        canStartRegex = (braces.pop() ?? 'block') === 'block';
        statementStart = canStartRegex;
      } else if (token === '=>') {
        body = 'expression-body';
        canStartRegex = true;
        statementStart = false;
      } else if (token === ';') {
        canStartRegex = true;
        statementStart = true;
      } else if (token === '++' || token === '--') {
        // Prefix operators still need an operand; postfix operators finish one.
        statementStart = false;
      } else if (
        token === ']' ||
        token === '.' ||
        token === '?.' ||
        token === 'literal' ||
        /^\d/.test(token)
      ) {
        canStartRegex = false;
        statementStart = false;
      } else if (JS_WORD_START.test(token[0] ?? '')) {
        if (!member && (token === 'function' || token === 'class')) {
          const expression = !statementStart;

          if (token === 'function') {
            pendingFunction = expression ? 'function-expression' : 'function-declaration';
          } else {
            pendingClass = expression ? 'expression-body' : 'block';
          }
        }
        canStartRegex =
          !member &&
          (EXPRESSION_KEYWORDS.has(token) ||
            CONTROL_HEADERS.has(token) ||
            STATEMENT_KEYWORDS.has(token));
        statementStart =
          !member &&
          (STATEMENT_KEYWORDS.has(token) ||
            ((token === 'export' || token === 'default' || token === 'async') && statementStart));
      } else {
        canStartRegex = true;
        statementStart = false;
      }
      previous = member && JS_WORD_START.test(token[0] ?? '') ? 'literal' : token;
    },
  };
}

/**
 * Reads `a, b as c` (or `a, b: c` for a destructured require) into the names each entry binds.
 * Inline `type` markers and comments drop out; a `*` or a nested pattern binds nothing here.
 */
function parseNameList(list: string | undefined, renamer: RegExp): BoundName[] {
  return (list ?? '')
    .replace(COMMENT, '')
    .split(',')
    .flatMap((entry) => {
      const [imported, local = imported] = entry
        .trim()
        .replace(/^type\s+/, '')
        .split(renamer);

      // Keep the imported default token even when the local binding has a different name.
      return imported && local && IDENTIFIER_ONLY.test(local)
        ? [{ local, importedName: imported }]
        : [];
    });
}

/**
 * The file a Python module names: leading dots climb from the importing file's package (one dot
 * is its own directory), and the dotted rest is a path. A bare `from . import x` names the
 * package's `__init__.py`.
 */
function pythonModulePath(module: string): string {
  const dots = /^\.*/.exec(module)?.[0].length ?? 0;
  const path = module.slice(dots).split('.').filter(Boolean).join('/');
  const prefix = dots === 0 ? '' : dots === 1 ? './' : '../'.repeat(dots - 1);

  return `${prefix}${path || '__init__'}.py`;
}
